import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { fmtInt, type FunnelStep } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';
import { SEGMENT_COLORS } from './chartTokens';

/**
 * El embudo de captura: una barra por escalón sobre el primero, con la nota
 * dentro y la cifra y la caída a la derecha. Un escalón ausente se dice
 * («no se preguntó»), no se dibuja como cero. Las barras crecen al entrar.
 */
export function FunnelBars({ steps, title, aside, note, className }: { steps: FunnelStep[]; title?: string; aside?: string; note?: string; className?: string }) {
  const t = useT();
  const { ref, p } = useScrollRamp();
  const top = steps[0]?.value || 1;
  return (
    <section ref={ref} className={cn('flex flex-col gap-4 rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      {(title || aside) && (
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          {title && <h3 className="text-[22px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{title}</h3>}
          {aside && <span className="text-[13px]" style={{ color: 'var(--color-dash-fg-muted)' }}>{aside}</span>}
        </div>
      )}
      {steps.map((s) => {
        const w = s.value == null ? 0 : Math.max(2, (s.value / top) * 100) * p;
        const fill = s.key === 'prizesDelivered' ? SEGMENT_COLORS.delivered : s.key === 'leads' ? 'var(--color-dash-lime)' : 'var(--color-dash-gray)';
        const insideFg = s.key === 'prizesDelivered' ? '#ffffff' : 'var(--color-dash-fg-2)';
        return (
          <div key={s.key} className="flex items-center gap-3">
            <span className="min-w-[74px] shrink basis-[150px] text-[15px] font-semibold" style={{ color: s.absent ? 'var(--color-dash-night-fg-muted)' : 'var(--color-dash-fg)' }}>
              {t(`dash.funnel.${s.key}`)}
            </span>
            <div className="relative h-[34px] min-w-[40px] flex-1 basis-[120px] overflow-hidden rounded-lg" style={{ background: 'var(--color-dash-paper-3)' }}>
              {!s.absent && (
                <div className="absolute inset-y-0 left-0 rounded-lg transition-[width] duration-[600ms] [transition-timing-function:cubic-bezier(0.2,0,0,1)]" style={{ width: `${w.toFixed(1)}%`, background: fill }} />
              )}
              <span className="absolute inset-y-0 left-3 flex items-center whitespace-nowrap text-xs" style={{ color: s.absent ? 'var(--color-dash-night-fg-muted)' : insideFg }}>
                {t(`dash.funnel.note.${s.key}`)}
              </span>
            </div>
            <span className="flex shrink-0 items-baseline justify-end gap-[9px] whitespace-nowrap">
              <span className="dash-mono font-bold" style={{ fontSize: s.absent ? 13 : 20, color: s.absent ? 'var(--color-dash-night-fg-muted)' : 'var(--color-dash-fg)' }}>
                {s.absent ? t('dash.funnel.notAsked') : fmtInt(Math.round((s.value ?? 0) * p))}
              </span>
              <span className="min-w-[52px] text-right text-xs" style={{ color: s.drop != null && s.drop > 45 ? 'var(--color-dash-orange)' : 'var(--color-dash-fg-muted)' }}>
                {s.drop == null ? '' : s.drop > 0 ? `−${s.drop}%` : `+${Math.abs(s.drop)}%`}
              </span>
            </span>
          </div>
        );
      })}
      {note && (
        <p className="border-t pt-3.5 text-[13px] leading-[1.55] [text-wrap:pretty]" style={{ color: 'var(--color-dash-fg-2)', borderColor: 'var(--color-dash-track)' }}>{note}</p>
      )}
    </section>
  );
}
